<template>
  <div class="">

    <div v-if="waitingForSharedWheel"
      class="fixed inset-0 flex items-center justify-center bg-white dark:bg-stone-900 z-50">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
    </div>

    <div v-if="!wheelIsShared && !waitingForSharedWheel" class="xl:min-h-screen xl:max-h-screen flex flex-col">
      <toolbar v-on:show-snackbar-message="showSnackbarMessage" v-on:reset-wheel="resetWheel"
        v-on:open-open-dialog="openOpenDialog" v-on:open-save-dialog="openSaveDialog"
        v-on:open-share-dialog="openShareDialog" v-on:open-customize-dialog="openCustomizeDialog"
        v-on:open-sheet-dialog="openSheetDialog" v-on:open-account-dialog="openAccountDialog"
        v-on:open-theme-dialog="openThemeDialog" v-on:set-locale="setLocale"></toolbar>
      <section class="flex-grow flex flex-col" :class="{ 'h-screen': fullScreen }">
        <div class="flex-grow xl:grid xl:grid-cols-12 xl:gap-4" :class="{ 'h-full': fullScreen }">
          <!-- Main content area (title/description + wheel) in 9 column container -->
          <div class="xl:col-span-9 mb-0" :class="{ 'h-[calc(100vh-96px)]': !fullScreen && !windowXL }">
            <!-- Inner 9-column grid for title/description and wheel -->
            <div class="h-full xl:grid xl:grid-cols-9 xl:gap-4">
              <!-- Title and description - 3 columns in the inner grid -->
              <div class="xl:col-span-3">
                <titleAndDescription v-on:editTitleAndDescription="openTitleAndDescriptionDialog"
                  :editable="!fullScreen" class="xl:h-full" />
              </div>

              <!-- Spinning wheel - 6 columns in the inner grid -->
              <div class="xl:col-span-6 flex justify-center items-center h-[66vh] xl:h-full">
                <spinningwheel v-if="hasWindow" ref="spinningwheelRef" v-on:wheel-started="wheelStarted"
                  v-on:name-changed="nameChanged" v-on:wheel-stopped="wheelStopped"
                  :class="{ 'max-w-full': fullScreen }"></spinningwheel>
                <div v-else class="w-full h-full"></div>
              </div>
            </div>
          </div>

          <!-- Wheel config / name tabs - 3 columns of the outer grid -->
          <div class="h-screen xl:col-span-3" v-show="!fullScreen">
            <name-tabs v-if="hasWindow" class="h-full name-tabs-container"
              v-on:reset-wheel-rotation="resetWheelRotation" />
            <div v-else class="h-full name-tabs-container"></div>
          </div>
        </div>
      </section>
    </div>

    <div v-if="wheelIsShared" class="min-h-screen flex flex-col">
      <section class="flex-grow flex flex-col">
        <div class="flex-grow xl:grid xl:grid-cols-12 xl:gap-6">
          <!-- Title, description, and controls -->
          <div class="xl:col-span-3 font-quicksand flex flex-col p-4 xl:p-6 pb-0">
            <div class="">
              <h1 v-if="wheelTitle" class="text-3xl font-bold text-stone-900 dark:text-white mb-4">{{ wheelTitle }}</h1>
              <p v-if="wheelDescription" class="text-stone-600 dark:text-stone-400 mb-6 leading-relaxed">{{
                wheelDescription }}</p>
            </div>

            <div class="space-y-4">
              <button v-if="!wheelIsCopyable" :disabled="wheelIsBusy" @click="createYourOwn" class="btn btn-primary">
                <i class="fas fa-pencil-alt mr-2"></i>{{ t('app.Create your own') }}
              </button>
              <button v-if="wheelIsCopyable" :disabled="wheelIsBusy" @click="copyWheel" class="btn btn-primary">
                <i class="far fa-clone mr-2"></i>{{ t('app.Copy this wheel') }}
              </button>
              <muteToggle />
            </div>
          </div>

          <!-- Spinning wheel - uses 6 columns with full height -->
          <div class="xl:col-span-6 flex justify-center items-center h-[66vh] xl:h-full">
            <spinningwheel v-if="hasWindow" ref="spinningwheelRef" v-on:wheel-started="wheelStarted"
              v-on:name-changed="nameChanged" v-on:wheel-stopped="wheelStopped" class="w-full h-full max-w-full">
            </spinningwheel>
            <div v-else class="w-full h-full"></div>
          </div>
          <div class="xl:col-span-3"></div>
        </div>
      </section>
    </div>

    <hr>

    <aboutCards v-if="!fullScreen" />
    <HomeContent v-if="!fullScreen" />
    <ProTips v-if="!fullScreen" />

    <app-footer v-if="!fullScreen" />

    <template v-if="hasWindow">
      <opendialog ref="opendialogRef" v-on:show-snackbar-message="showSnackbarMessage"
        v-on:start-wait-animation="startWaitAnimation" v-on:stop-wait-animation="stopWaitAnimation"
        v-on:reset-wheel-rotation="resetWheelRotation" v-on:reset-address-bar="resetAddressBar"
        v-on:open-account-dialog="openAccountDialog"></opendialog>
      <savedialog ref="savedialogRef" v-on:show-snackbar-message="showSnackbarMessage"
        v-on:start-wait-animation="startWaitAnimation" v-on:stop-wait-animation="stopWaitAnimation"
        v-on:reset-address-bar="resetAddressBar" v-on:open-account-dialog="openAccountDialog"></savedialog>
      <optionsdialog ref="optionsdialogRef" v-on:show-snackbar-message="showSnackbarMessage"></optionsdialog>
      <sharedialog ref="sharedialogRef" v-on:show-snackbar-message="showSnackbarMessage"
        v-on:start-wait-animation="startWaitAnimation" v-on:stop-wait-animation="stopWaitAnimation"
        v-on:open-account-dialog="openAccountDialog"></sharedialog>
      <sheetdialog ref="sheetdialogRef" v-on:show-snackbar-message="showSnackbarMessage">
      </sheetdialog>
      <accountdialog ref="accountdialogRef" v-on:show-snackbar-message="showSnackbarMessage"
        v-on:start-wait-animation="startWaitAnimation" v-on:stop-wait-animation="stopWaitAnimation"></accountdialog>
      <winnerdialog ref="winnerdialogRef" v-on:remove-entry="removeEntry" v-on:remove-entry-all="removeEntryAll"
        v-on:hide-entry="hideEntry"></winnerdialog>
      <titleAndDescriptionDialog ref="titleAndDescriptionDialogRef"
        v-on:setTitleAndDescription="setTitleAndDescription">
      </titleAndDescriptionDialog>

      <themedialog ref="themedialogRef"></themedialog>

      <winneranimation ref="winneranimationRef">
      </winneranimation>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useWheelStore } from '@/stores/useWheelStore';
import { usePreferencesStore } from '@/stores/usePreferencesStore';
import { useUserStore } from '@/stores/useUserStore';
import { useHardwareStore } from '@/stores/useHardwareStore';
import { useAdminStore } from '@/stores/useAdminStore';

const { t, locale } = useI18n();

import toolbar from '@components/ui/toolbar.vue';
import spinningwheel from '@components/wheel/spinningwheel.vue';
import nameTabs from '@components/ui/nameTabs.vue';
import aboutCards from '@components/shared/aboutCards.vue';
import HomeContent from '@components/shared/HomeContent.vue';
import ProTips from '@components/shared/ProTips.vue';
import opendialog from '@components/dialogs/opendialog.vue';
import savedialog from '@components/dialogs/savedialog.vue';
import optionsdialog from '@components/dialogs/optionsdialog.vue';
import sharedialog from '@components/dialogs/sharedialog.vue';
import sheetdialog from '@components/dialogs/sheetdialog.vue';
import accountdialog from '@components/dialogs/accountdialog.vue';
import winnerdialog from '@components/dialogs/winnerdialog.vue';
import titleAndDescriptionDialog from '@components/dialogs/titleAndDescriptionDialog.vue';
import titleAndDescription from '@components/ui/titleAndDescription.vue';
import muteToggle from '@components/ui/muteToggle.vue';
import winneranimation from '@components/wheel/winneranimation.vue';
import themedialog from '@components/dialogs/themedialog.vue';
import appFooter from '@components/ui/appFooter.vue';
import * as ConfettiLauncher from '@services/wheel/ConfettiLauncher.js';
import * as Util from '@utils/Util.js';
import * as Audio from '@services/audio/audio.js';
import * as Locales from '@utils/Locales.js';
import * as i18nSetup from '@/i18n-setup.js';
import { getCanonicalUrl } from '@services/SeoService.js';
import { useSeo } from '@/composables/useSeo';
import { useDialogs } from '@/composables/useDialogs';

// Setup composables
const wheelStore = useWheelStore();
const preferencesStore = usePreferencesStore();
const userStore = useUserStore();
const hardwareStore = useHardwareStore();
// Admin store might not be used directly but initialized by userStore
const route = useRoute();
const router = useRouter();

// Get store state and getters
const wheelConfig = computed(() => wheelStore.wheelConfig);
const fullScreen = computed(() => hardwareStore.isFullScreen);
const wheelIsBusy = computed(() => wheelStore.wheelIsBusy);
const darkMode = computed(() => preferencesStore.darkMode);
const wheelIsShared = computed(() => wheelStore.wheelIsShared);
const wheelIsCopyable = computed(() => wheelStore.wheelIsCopyable);
const wheelInitStatus = computed(() => wheelStore.wheelInitStatus);
const wheelInitError = computed(() => wheelStore.wheelInitError);

// Computed properties
const hasSharedPath = computed(() => !!route.params.sharedWheelPath);
const waitingForSharedWheel = computed(() => {
  return hasSharedPath.value && wheelInitStatus.value !== 'ready' && !wheelInitError.value;
});

// Use the global composer in composition-only mode

const canonicalUrl = computed(() => getCanonicalUrl(route.path, locale.value));

const seoTitle = computed(() => {
  const isSharedWheel = wheelIsShared.value;
  const currentWheelTitle = wheelConfig.value?.title || '';

  if (isSharedWheel && currentWheelTitle) {
    return `${currentWheelTitle} | Spin the Names`;
  }
  return 'Spin the Names | Random Name Picker Wheel';
});

const seoDescription = computed(() => {
  const isSharedWheel = wheelIsShared.value;
  const currentWheelTitle = wheelConfig.value?.title || '';
  const currentWheelDescription = wheelConfig.value?.description || '';

  if (isSharedWheel && currentWheelTitle) {
    return currentWheelDescription
      ? `${currentWheelDescription} - Create your own random name picker wheel.`
      : `Spin the wheel to randomly select from ${wheelConfig.value.entries.length} entries. Create your own random name picker wheel.`;
  }
  return t('app.Free and easy to use');
});

const seoStructuredData = computed(() => {
  const isSharedWheel = wheelIsShared.value;

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    'name': 'Spin the Names',
    'description': seoDescription.value,
    'url': canonicalUrl.value,
    'applicationCategory': 'UtilityApplication',
    'offers': {
      '@type': 'Offer',
      'price': '0',
      'priceCurrency': 'USD'
    }
  };

  if (isSharedWheel && wheelConfig.value?.entries && wheelConfig.value.entries.length > 0) {
    structuredData.potentialAction = {
      '@type': 'UseAction',
      'target': canonicalUrl.value,
      'description': `Spin a wheel with ${wheelConfig.value.entries.length} entries`
    };
  }

  return structuredData;
});

// Provide SSR-friendly default SEO for homepage and general wheel page
useSeo({
  title: () => seoTitle.value,
  description: () => seoDescription.value,
  canonicalUrl: () => canonicalUrl.value,
  structuredData: () => seoStructuredData.value,
  ogType: 'website'
});

// Setup refs for components
const spinningwheelRef = ref(null);
const winneranimationRef = ref(null);

// Use dialogs composable to manage dialog state and actions
const {
  opendialogRef,
  savedialogRef,
  optionsdialogRef,
  sharedialogRef,
  sheetdialogRef,
  accountdialogRef,
  winnerdialogRef,
  titleAndDescriptionDialogRef,
  themedialogRef,
  openOpenDialog,
  openSaveDialog,
  openShareDialog,
  openCustomizeDialog,
  openSheetDialog,
  openAccountDialog,
  openThemeDialog,
  openTitleAndDescriptionDialog
} = useDialogs();

// Setup reactive data
const waitAnimation = ref({});
const hasWindow = typeof window !== 'undefined';
const hasNavigator = typeof navigator !== 'undefined';
const windowXL = ref(hasWindow ? window.innerWidth >= 1280 : false); // XL breakpoint in Tailwind is 1280px

const wheelTitle = computed(() => {
  if (wheelConfig.value?.showTitle) {
    return wheelConfig.value.title;
  }
  return null;
});

const wheelDescription = computed(() => {
  if (wheelConfig.value?.showTitle) {
    return wheelConfig.value.description;
  }
  return null;
});

// Setup translation function for store
const translateMe = (val) => {
  return t(val);
};

// Component setup
onMounted(async () => {
  // Inject translation-function-shim if needed, usually handled inside the store module now via imports but lets keep compatibility if needed? 
  // The Pinia store imports i18n globally. So we might not need to inject 't'.
  // But verify if valid.

  // Kick off auth initialization, but don't block wheel boot
  const userInitPromise = userStore.initialize().catch(() => false);

  // Handle admin access error messages from query parameters
  if (route.query.error && route.query.message) {
    const errorType = route.query.error;
    const message = route.query.message;

    // Show appropriate error message based on error type
    let displayMessage = message;
    switch (errorType) {
      case 'login_required':
        displayMessage = t('app.Please log in to access admin features');
        break;
      case 'access_denied':
        displayMessage = t('app.Admin access required');
        break;
      case 'validation_error':
        displayMessage = t('app.Unable to verify admin permissions');
        break;
      default:
        displayMessage = message;
    }

    // Show error message to user
    setTimeout(() => {
      showSnackbarMessage(displayMessage);
    }, 1000); // Delay to ensure UI is ready

    // Clean up URL by removing query parameters
    router.replace({ path: route.path });
  }

  try {
    await Promise.all([
      i18nSetup.setLang(route.params.lang),
      wheelStore.loadInitialWheel(route.params.sharedWheelPath)
    ]);
  }
  catch (ex) {
    Util.trackException(ex);
    alert(ex);
  }

  preferencesStore.loadPreferences();
  setDocLangAndTitle();
  startFullscreenDetection();
  startOnlineDetection();
  refreshWheelOnFontLoad();
  warnInternetExplorerUsers();

  if (hasWindow) {
    window.addEventListener('resize', updateWindowSize);
    updateWindowSize();
  }

  // If returning from Google redirect, show the account dialog once
  try {
    const flag = sessionStorage.getItem('showAccountDialogAfterRedirect');
    if (flag === '1') {
      sessionStorage.removeItem('showAccountDialogAfterRedirect');
      setTimeout(() => openAccountDialog(), 0);
    }
  } catch { }
});

// Clean up resize listener on unmount and clear wheel canvas
onBeforeUnmount(() => {
  if (hasWindow) {
    window.removeEventListener('resize', updateWindowSize);
  }

  // Force complete reset of the wheel to clear any cached drawing state
  if (spinningwheelRef.value && typeof spinningwheelRef.value.forceCompleteReset === 'function') {
    spinningwheelRef.value.forceCompleteReset();
  }
});

// Watch for changes
watch(wheelConfig, (newValue) => {
  if (darkMode.value !== undefined && wheelConfig.value) {
    Util.updateColorStyles(darkMode.value, '#777', wheelConfig.value.pageBackgroundColor);
    Audio.preloadSounds(newValue.duringSpinSound, newValue.afterSpinSound);
    setDocLangAndTitle();
  }
});

watch(darkMode, () => {
  if (wheelConfig.value) {
    Util.updateColorStyles(darkMode.value, '#777', wheelConfig.value.pageBackgroundColor);
  }
});

watch(locale, () => {
  displayLanguageTip();
});

watch(fullScreen, () => {
  // Update the window size calculation when fullscreen state changes
  nextTick(() => {
    updateWindowSize();
  });
});

// Methods
function startFullscreenDetection() {
  if (typeof document === 'undefined') return;
  document.addEventListener('fullscreenchange', event => {
    hardwareStore.fullScreenChanged();
  });
}

function startOnlineDetection() {
  if (!hasWindow || !hasNavigator) return;
  window.addEventListener('online', event => {
    hardwareStore.setOnline(navigator.onLine);
  });
  window.addEventListener('offline', event => {
    hardwareStore.setOnline(navigator.onLine);
  });
}

// Removed 2-day auto-reloader for hidden pages

function displayLanguageTip() {
  if (!hasNavigator || !hasWindow) return;
  if (wheelIsShared.value) return;
  const tipLocale = Locales.getLangTipLocale(locale.value, navigator.languages);
  if (tipLocale) {
    setTimeout(async () => {
      try {
        const file = Locales.getMessagesFileName(tipLocale);
        const response = await fetch(`/locales/${file}`);
        if (!response.ok) {
          throw new Error(`Failed to load locale: ${response.status} ${response.statusText}`);
        }
        const messages = await response.json();
        const msg = messages['app']['Click the Language menu'];
        showSnackbarMessage(msg);
      } catch (error) {
        console.error(`Error loading locale tip ${tipLocale}:`, error);
      }
    }, 3000);
  }
}

function setDocLangAndTitle() {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('lang', locale.value);
}

function refreshWheelOnFontLoad() {
  if (typeof document === 'undefined' || !document.fonts) {
    return;
  }
  document.fonts.ready.then(function () {
    if (spinningwheelRef.value) {
      spinningwheelRef.value.refresh();
    }
  });
}

function warnInternetExplorerUsers() {
  if (!hasWindow) return;
  if (Util.browserIsIE() && window.$ui?.dialog?.alert) {
    // Assuming $ui is globally available through plugin
    window.$ui.dialog.alert({
      title: t('app.Warning'),
      message: t('app.It looks like you are using Internet Explorer'),
      type: 'is-warning',
      hasIcon: true,
      ariaRole: 'alertdialog',
      ariaModal: true
    });
  }
}

function resetWheel() {
  wheelStore.resetWheel();
  if (spinningwheelRef.value) {
    spinningwheelRef.value.resetRotation();
  }
  showSnackbarMessage(t('app.Loaded default names and options'));
  resetAddressBar();
}


function nameChanged() {
  if (wheelIsBusy.value && wheelConfig.value.shouldPlayTicks()) {
    Audio.playTick(wheelConfig.value.duringSpinSoundVolume);
  }
}

function wheelStarted() {
  Audio.startMusic(wheelConfig.value.duringSpinSound, wheelConfig.value.duringSpinSoundVolume);
}

function wheelStopped(winningEntry) {
  Audio.fadeMusic();
  wheelStore.addWinner(winningEntry);

  if (wheelConfig.value.animateWinner) {
    winneranimationRef.value.show(winningEntry);
  }

  if (wheelConfig.value.launchConfetti) {
    ConfettiLauncher.launch(wheelConfig.value.getCoalescedColors());
  }

  if (wheelConfig.value.displayWinnerDialog) {
    winnerdialogRef.value.show(winningEntry);
  }

  if (wheelConfig.value.autoRemoveWinner) {
    const duration = 5;
    wheelStore.setWheelBusy(true);
    setTimeout(() => removeEntry(winningEntry), 1000 * duration);
    setTimeout(() => wheelStore.setWheelBusy(false), 1000 * duration);

    if (hasWindow && window.$ui?.toast) {
      let toast = window.$ui.toast.open({
        message: t('app.Removing winner in', { seconds: duration }),
        duration: 1000 * duration,
        queue: false
      });

      for (let i = duration - 1; i > 0; i--) {
        setTimeout(() => (toast.message = t('app.Removing winner in', { seconds: i })),
          1000 * (duration - i));
      }
    }
  }

  Audio.playAfterSpin(
    winningEntry.sound || wheelConfig.value.afterSpinSound,
    winningEntry.text,
    wheelConfig.value.afterSpinSoundVolume,
    locale.value
  );
}

function showSnackbarMessage(msg) {
  if (!hasWindow || !window.$ui?.toast) return;
  window.$ui.toast.open({ message: Util.escapeHtml(msg), duration: 6000 });
}

function startWaitAnimation() {
  if (!hasWindow || !window.$ui?.loading || typeof window.$ui.loading.open !== 'function') {
    console.error('UIService loading is not available on window.$ui.');
    // Optionally, you could add a fallback UI here if needed
    return;
  }
  waitAnimation.value = window.$ui.loading.open({ container: null });
}

function stopWaitAnimation() {






  // Check if waitAnimation.value is a valid loading instance before calling close
  if (waitAnimation.value && typeof waitAnimation.value.close === 'function') {
    waitAnimation.value.close();

  } else {

    // Log the value again in the else block for clarity

  }
}

function removeEntry(entry) {
  if (wheelConfig.value.playClickWhenWinnerRemoved) {
    Audio.playClick(wheelConfig.value.afterSpinSoundVolume);
  }
  wheelStore.removeEntry(entry);
  const msg = t('app.Removed', { name: entry.text });
  showSnackbarMessage(msg);
}

function removeEntryAll(entry) {
  if (wheelConfig.value.playClickWhenWinnerRemoved) {
    Audio.playClick(wheelConfig.value.afterSpinSoundVolume);
  }
  wheelStore.removeEntryAll(entry);
  const msg = t('app.Removed', { name: entry.text });
  showSnackbarMessage(msg);
}

function hideEntry(entry) {
  if (wheelConfig.value.playClickWhenWinnerRemoved) {
    Audio.playClick(wheelConfig.value.afterSpinSoundVolume);
  }
  wheelStore.hideEntry(entry);
}

function authError(ex) {
  Util.trackException(ex);
  if (!hasNavigator) return;
  Util.trackEvent('AuthError', ex.toString(), navigator.userAgent);
  showSnackbarMessage(t('accountdialog.Login failed'));
}

function resetWheelRotation() {
  if (spinningwheelRef.value) {
    spinningwheelRef.value.resetRotation();
  }
}

function resetAddressBar() {
  if (!hasWindow) return;
  const url = Locales.getRelativeUrl(location.hostname, locale.value);
  window.history.replaceState({}, '', url);
}

async function setLocale(localeValue) {
  router.push(`/${Util.getNonEnglishLocale(localeValue)}`).catch(err => { });
  await i18nSetup.setLang(localeValue);
  setDocLangAndTitle();
}

async function createYourOwn() {
  wheelStore.setUnshared();
  nextTick(async function () {
    await wheelStore.resetWheel();
  });
  router.push(`/${Util.getNonEnglishLocale(locale.value)}`);
}

function copyWheel() {
  wheelStore.clearWinners();
  wheelStore.setUnshared();
  router.push(`/${Util.getNonEnglishLocale(locale.value)}`);
}


function setTitleAndDescription(params) {
  wheelStore.setWheelTitle(params.title);
  wheelStore.setWheelDescription(params.description);
}

function updateWindowSize() {
  if (!hasWindow) return;
  // Update windowXL based on current window width
  // XL breakpoint in Tailwind is 1280px
  windowXL.value = window.innerWidth >= 1280;

  // Notify the spinningwheel component to update its size
  nextTick(() => {
    if (spinningwheelRef.value) {
      spinningwheelRef.value.updateCanvasSize();
    }
  });
}
</script>

<style>
.can-go-dark {}

.name-tabs-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 300px;
  max-height: 80vh;
  overflow: auto;
}
</style>
